import contentfulLanguages from '@shared/locales.json';

const parseEnvStringToStringArray = (value?: string): string[] => {
    try {
        if (!value?.trim()) {
            return [];
        }
        const trimmed = value.trim();
        const parsed: unknown = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
            return parsed.filter(
                (item): item is string => typeof item === 'string' && item.trim().length > 0,
            );
        }
        return [];
    } catch {
        return [];
    }
};

export enum BrandProductCode {
    PP = 'PP',
    LK = 'LK',
}

export const isBrandProductCode = (value: string): value is BrandProductCode =>
    value === BrandProductCode.PP || value === BrandProductCode.LK;

const DEFAULT_RECAPTCHA_MIN_SCORE = 0.5;

const parseRecaptchaMinScore = (raw: string | undefined): number => {
    if (raw === undefined || raw.trim() === '') {
        return DEFAULT_RECAPTCHA_MIN_SCORE;
    }

    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1
        ? parsed
        : DEFAULT_RECAPTCHA_MIN_SCORE;
};

const parseHostnameList = (raw: string | undefined): string[] =>
    (raw ?? '')
        .split(',')
        .map((hostname) => hostname.trim().toLowerCase())
        .filter(Boolean);

/** Fetchify API paths for email and phone validation */
const FETCHIFY_API_PATHS = {
    EMAIL_VALIDATION: '/email/1.0/validate',
    PHONE_VALIDATION: '/phone/1.0/validate',
} as const;

/** Default Fetchify API base URL */
const DEFAULT_FETCHIFY_API_BASE = 'https://api.craftyclicks.co.uk';

/** Default Fetchify request timeout in milliseconds */
const DEFAULT_FETCHIFY_TIMEOUT_MS = 3000;

export const config = {
    branch: process.env.GIT_BRANCH,
    environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
    siteUrl: process.env.NEXT_PUBLIC_SITE_URL,
    ppProdUrl: process.env.PP_PROD_URL,
    memberHelpUrl: process.env.NEXT_PUBLIC_MEMBERHELP_URL ?? '',
    contentful: {
        spaceId: process.env.CONTENTFUL_SPACE_ID,
        accessToken: process.env.CONTENTFUL_ACCESS_TOKEN,
        environment: process.env.CONTENTFUL_ENVIRONMENT,
        apiToken: process.env.CONTENTFUL_API_TOKEN,
        previewAccessToken: process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN,
        previewSecret: process.env.CONTENTFUL_PREVIEW_SECRET,
        /** Optional custom Contentful API host (defaults to contentful.com) */
        host: process.env.CONTENTFUL_HOST,
        /** Optional custom Contentful preview API host (defaults to preview.contentful.com) */
        previewHost: process.env.CONTENTFUL_PREVIEW_HOST,
        mobileAppSpaceId: process.env.CONTENTFUL_MOBILE_APP_SPACE_ID,
        mobileAppAccessToken: process.env.CONTENTFUL_MOBILE_APP_ACCESS_TOKEN,
        mobileAppEnvironment: process.env.CONTENTFUL_MOBILE_APP_ENVIRONMENT,
    },
    gtm: {
        id: process.env.NEXT_PUBLIC_GTM_ID,
    },
    optimizely: {
        id: process.env.NEXT_PUBLIC_OPTIMIZELY_ID,
    },
    datadog: {
        site: process.env.NEXT_PUBLIC_DATADOG_SITE,
        infoLogsSwitch: process.env.NEXT_PUBLIC_DATADOG_INFOLOGS_SWITCH,
        excludedActivityLinks: [
            /^https?:\/\/.*\.googletagmanager\.com\/.*/,
            /^https?:\/\/.*\.optimizely\.com\/.*/,
            /^https?:\/\/logx\.optimizely\.com\/.*/,
            /^https?:\/\/.*\/heapApi\.js$/,
            /^https?:\/\/.*\.heap-api\.com\/.*/,
            /^https?:\/\/.*\.facebook\.com\/.*/,
            /^https?:\/\/.*\.google\.co\.uk\/.*/,
            /^https?:\/\/.*\.google\.com\/.*/,
            /^https?:\/\/analytics\.google\.com\/.*/,
            /^https?:\/\/stats\.g\.doubleclick\.net\/.*/,
            /^https?:\/\/pagead2\.googlesyndication\.com\/.*/,
            /^https?:\/\/www\.gstatic\.com\/.*/,
            /^https?:\/\/[a-z]\.clarity\.ms\/collect.*/,
            /^http?:\/\/n\.wistiaextension\.com\/.*/,
            /^https?:\/\/cdn\.growthbook\.io\/.*/,
            /^https?:\/\/.*\.ceros\.com\/.*/,
            /^https?:\/\/.*\.anchor\.fm\/.*/,
            /^https?:\/\/app\.shop\.pe\/app\/identity\/status\/.*/,
            /^https?:\/\/shopper\.shop\.pe\/pixel\.png.*/,
            /^https?:\/\/cdn-account\.optimonk\.com\/\d+\/campaigns\.json.*/,
            /^https?:\/\/cdn-limit\.optimonk\.com\/public\/\d+\/limit\.json.*/,
            /^https?:\/\/prioritypass\.pxf\.io\/xc\/.*/,
            /^https?:\/\/events\.launchdarkly\.com\/events\/.*/,
            /^https?:\/\/geolocation\.onetrust\.com\/.*/,
            /^https?:\/\/.*\.smct\.io\/.*/,
        ],

        prefix: 'MyPriorityPassFlow', // will be changed later
        // CefSharp / Outlook Safe Links scanners — not our app. See DatadogInitializer.
        ignoredErrors: [{ messageContains: 'Object Not Found Matching Id:', sampleRate: 0 }],
    },
    launchdarkly: {
        sdkKey: process.env.LAUNCHDARKLY_SDK_KEY || '',
        backupFlags: process.env.LAUNCHDARKLY_BACKUP_FLAGS || '',
        ldInitTimeoutSeconds: Number(process.env.LAUNCHDARKLY_INIT_TIMEOUT_SECONDS) || 5,
        defaultFlagValue: process.env.LAUNCHDARKLY_DEFAULT_FLAG_VALUE ?? 'false',
    },
    language: {
        default: contentfulLanguages?.items.find((locale) => locale.default)?.code,
        supported: contentfulLanguages?.items.map((locale) => locale.code),
        salesforce: contentfulLanguages?.items.reduce(
            (acc, locale) => ({
                ...acc,
                [locale.code]: locale.salesforceCode,
            }),
            {},
        ),
    },
    algolia: {
        appId: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
        searchApiKey: process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY,
        resultItemsPerPage: process.env.ALGOLIA_SEARCH_RESULT_ITEMS_BY_PAGE,
        resultItemsInDropdown: process.env.ALGOLIA_SEARCH_RESULT_ITEMS_IN_DROPDOWN,
        indexName: process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME ?? '',
        globalSearchIndexName: process.env.ALGOLIA_GLOBAL_SEARCH_INDEX_NAME ?? '',
        populateApiKey: process.env.ALGOLIA_POPULATE_API_KEY ?? '',
        apiKey: process.env.ALGOLIA_API_KEY ?? '',
    },
    capcha: {
        /** reCAPTCHA Enterprise (Assessments API) — server-side only. */
        recaptchaEnterprise: {
            v3SiteKey: process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY
                ? process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY
                : '',
            projectId: process.env.RECAPTCHA_ENTERPRISE_PROJECT_ID ?? '',
            apiKey: process.env.RECAPTCHA_ENTERPRISE_API_KEY ?? '',
            /**
             * Help-centre's own policy key, so its thresholds can be managed separately from the
             * shared key. Falls back to the shared key when unset, so other apps are unaffected.
             */
            helpCentreV3SiteKey:
                process.env.NEXT_PUBLIC_HELP_CENTRE_RECAPTCHA_V3_SITE_KEY ||
                process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY ||
                '',
            /**
             * Lowest risk score accepted when Google reports no challenge outcome (a score-based
             * key). 0 disables the check. Unset or invalid falls back to the 0.5 Google suggests.
             */
            minScore: parseRecaptchaMinScore(process.env.RECAPTCHA_MIN_SCORE),
            /**
             * Comma-separated hostnames a token may have been minted for (the page that ran
             * reCAPTCHA); a subdomain of a listed domain is allowed. Empty disables the check.
             */
            allowedHostnames: parseHostnameList(process.env.RECAPTCHA_ALLOWED_HOSTNAMES),
        },
    },
    newLogin: {
        apiKey: process.env.NEW_LOGIN_API_KEY,
        nextAuthSecret: process.env.NEXTAUTH_SECRET,
        // Must be set to the canonical public-facing URL (e.g. https://www.prioritypass.com).
        nextAuthUrl: process.env.NEXTAUTH_URL,
        npdRefreshTime: 15 * 60 * 1000, // 15 minutes
        ppPlusUserAuthCookieMaxAgeInSeconds: Number(
            process.env.PP_PLUS_USER_AUTH_MAX_AGE_IN_SECONDS ?? 3600, // 1 hour
        ),
    },
    adminToken: {
        username: process.env.ADMIN_TOKEN_USERNAME,
        password: process.env.ADMIN_TOKEN_PASSWORD,
        productCode: process.env.ADMIN_TOKEN_PRODUCT_CODE,
        grantType: process.env.ADMIN_TOKEN_GRANT_TYPE,
        clientId: process.env.ADMIN_TOKEN_CLIENTID,
        clientSecret: process.env.ADMIN_TOKEN_SECRET,
    },
    mobileApp: {
        clientId: process.env.MOBILE_APP_CLIENT_ID,
        clientSecret: process.env.MOBILE_APP_CLIENT_SECRET,
    },
    salesforce: {
        authUrl: process.env.SALESFORCE_AUTH_URL,
        salesforceWebToLeadUrl: process.env.SALESFORCE_WEB_TO_LEAD_URL,
        webToLead: {
            campaignId: process.env.SALESFORCE_CAMPAIGN_ID,
            organizationId: process.env.SALESFORCE_ORG_ID,
            whoThisForFieldId: process.env.SALESFORCE_WHO_THIS_FOR_FIELD_ID,
            customField1Value: process.env.SALESFORCE_CUSTOM_FIELD_1_VALUE,
            customField2Value: process.env.SALESFORCE_CUSTOM_FIELD_2_VALUE,
            customField3Value: process.env.SALESFORCE_CUSTOM_FIELD_3_VALUE,
            customField1Id: process.env.SALESFORCE_CUSTOM_FIELD_1_ID,
            customField2Id: process.env.SALESFORCE_CUSTOM_FIELD_2_ID,
            customField3Id: process.env.SALESFORCE_CUSTOM_FIELD_3_ID,
        },
        case: {
            grantType: process.env.SALESFORCE_GRANT_TYPE,
            clientId: process.env.SALESFORCE_CLIENT_ID,
            clientSecret: process.env.SALESFORCE_CLIENT_SECRET,
            username: process.env.SALESFORCE_USERNAME,
            password: process.env.SALESFORCE_PASSWORD,
            url: process.env.SALESFORCE_CASE_URL,
            attachmentUrl: process.env.SALESFORCE_CASE_ATTACHMENT_URL,
        },
    },
    // TODO: Remove this from shared config
    apiUrls: {
        apiURL: process.env.API_URL,
        aggregateApiURL: process.env.AGGREGATE_API_URL,
        accessTokenURL: `${process.env.API_URL}/admin/token`,
        introspectTokenURL: `${process.env.API_URL}/admin/token/introspect`,
        sourceCodeURL: `${process.env.API_URL}/deal/source`,
        createProspectURL: `${process.env.API_URL}/registration/createProspect`,
        migrateAccountURL: `${process.env.API_URL}/registration/migrateExistingAccount`,
        deliveryChargeURL: `${process.env.API_URL}/deal/source/HandlingCharges`,
        bankCardNumberURL: `${process.env.API_URL}/deal/validator/BankCardNumber`,
        bankCardNumberVisaPhoenixURL: `${process.env.API_URL}/deal/validator/BankCardNumberVISAPhoenix`,
        authenticate: `${process.env.API_URL}/auth/authenticate`,
        newLoginURL: `${process.env.NEW_LOGIN_API_URL}/auth/login`,
        confirmMigrationURL: `${process.env.NEW_LOGIN_API_URL}/auth/confirm-consumer`,
        migrateConsumerURL: `${process.env.NEW_LOGIN_API_URL}/migration/migrate`,
        membershipTransferURL: `${process.env.NEW_LOGIN_API_URL}/migration/transfer-membership-to-consumer`,
        verifyOTPURL: `${process.env.NEW_LOGIN_API_URL}/otp/verify`,
        resendOTPURL: `${process.env.NEW_LOGIN_API_URL}/otp/resend`,
        getUserMembershipsWithBCURL: `${process.env.NEW_LOGIN_API_URL}/user/memberships`,
        paymentCardPopupStatusURL: `${process.env.NEW_LOGIN_API_URL}/payment/popup-status`,
        refreshTokenURL: `${process.env.NPD_REFRESH_API_URL}/session/refresh`,
        securityQuestions: `${process.env.API_URL}/auth/securityQuestions`,
        cardTypes: `${process.env.API_URL}/payment/cardTypes`,
        checkUsernameURL: `${process.env.API_URL}/auth/isUsernameAvailable`,
        usStatesURL: `${process.env.API_URL}/location/USStates`,
        convertProspectURL: `${process.env.API_URL}/registration/convertProspect`,
        sendPasswordResetEmail: `${process.env.API_URL}/auth/sendPasswordResetEmail`,
        sendUsernameReminder: `${process.env.API_URL}/auth/sendUsernameReminder`,
        securityQuestion: `${process.env.API_URL}/auth/securityQuestion`,
        authenticateSecurityAnswer: `${process.env.API_URL}/auth/authenticateSecurityAnswer`,
        updatePassword: `${process.env.API_URL}/auth/updatePassword`,
        memberRegistrationValidation: `${process.env.API_URL}/validation/memberRegistrationValidation`,
        activateByMembershipNumber: `${process.env.API_URL}/registration/associate`,
        createFullMemberAccount: `${process.env.API_URL}/registration/createFullMemberAccount`,
        consumerDetails: `${process.env.API_URL}/member/consumer`,
        getRateYourLounge: `${process.env.API_URL}/member/consumer`,
        getAllVisits: `${process.env.API_URL}/member/consumer`,
        consumerUrl: `${process.env.API_URL}/member/consumer`,
        registrationBusinessURL: `${process.env.API_URL}/registration/business`,
        userSearchApiUrl: `${process.env.NEW_LOGIN_API_URL}/user/personalized-catalogue-search`,
        outletSearchApiUrl: `${process.env.NEW_LOGIN_API_URL}/user/personalized-outlet-search`,
        legacySearchTokenURL: `${process.env.NEW_LOGIN_API_URL}/user/legacy-search-session`,
        legacyCatalogueSearchURL: `${process.env.NEW_LOGIN_API_URL}/user/legacy-catalogue-search`,
        legacyOutletSearchURL: `${process.env.NEW_LOGIN_API_URL}/user/legacy-outlet-search`,
    },
    BINWidget: {
        apiUrl: process.env.BIN_WIDGET_API_URL,
        signinUrl: process.env.BIN_WIDGET_SIGNIN_API_URL,
        signinEmail: process.env.BIN_WIDGET_SIGNIN_EMAIL,
        signinPassword: process.env.BIN_WIDGET_SIGNIN_PASSWORD,
    },
    consumerAggregateApiUrls: process.env.CONSUMER_AGGREGATE_API_URL
        ? {
              verification: `${process.env.CONSUMER_AGGREGATE_API_URL}/verifications`,
              getProductById: `${process.env.CONSUMER_AGGREGATE_API_URL}/products`,
              activateByPaymentCard: `${process.env.CONSUMER_AGGREGATE_API_URL}/register`,

              productSearchURL: `${process.env.CONSUMER_AGGREGATE_API_URL}/products/search`,
              startURL: `${process.env.CONSUMER_AGGREGATE_API_URL}/payments/workflow/start`,
              nextStepURL: `${process.env.CONSUMER_AGGREGATE_API_URL}/payments/workflow/nextStep`,
              getInventoryItems: `${process.env.CONSUMER_AGGREGATE_API_URL}/locations/inventoryItems`,
              subscriptions: `${process.env.CONSUMER_AGGREGATE_API_URL}/subscriptions`,
              paymentMethods: `${process.env.CONSUMER_AGGREGATE_API_URL}/paymentMethods`,
          }
        : undefined,
    fetchify: {
        apiKey: process.env.NEXT_PUBLIC_FETCHIFY_API_KEY,
        serverApiKey: process.env.FETCHIFY_API_KEY,
        apiBase: process.env.FETCHIFY_API_BASE ?? DEFAULT_FETCHIFY_API_BASE,
        requestTimeoutMs: Number(process.env.FETCHIFY_TIMEOUT_MS) || DEFAULT_FETCHIFY_TIMEOUT_MS,
        emailValidationPath: FETCHIFY_API_PATHS.EMAIL_VALIDATION,
        phoneValidationPath: FETCHIFY_API_PATHS.PHONE_VALIDATION,
    },
    // Server-only: never read this on the client, it is empty there. Server components must
    // resolve the default source code and pass it down explicitly.
    // DEPLOYMENT REQUIREMENT: set SOURCE_CODE on the server. A missing env var yields an empty
    // default and breaks organic join and static pages that fall back to it.
    sourceCode: process.env.SOURCE_CODE ?? '',
    cacheTime: (Number(process.env.STATIC_API_ENDPOINTS_CACHE_TIME_MINUTES) || 60) * 60,
    productCode: process.env.ADMIN_TOKEN_PRODUCT_CODE ?? BrandProductCode.PP,
    authTokenSecret: process.env.AUTH_TOKEN_SECRET ?? 'secret-1',
    tokenex: {
        tokenExId: process.env.TOKENEX_ID ?? '',
        clientSecretKey: process.env.TOKENEX_ID_SECRET ?? '',
        sessionDuration: Number(process.env.NEXT_PUBLIC_TX_SESSION_DURATION_IN_SECONDS ?? 1200),
        tokenScheme: process.env.TOKENEX_ID_TOKEN_SCHEME ?? '',
        scriptUrl: process.env.NEXT_PUBLIC_TOKENEX_SCRIPT_URL ?? '',
    },
    /**
     * AWS WAF intelligent-threat SDK (`challenge.js`). Empty URL disables the script (no-op).
     * Set per app/env from the web ACL Application integration → JavaScript SDK `src`.
     * Optional cookie domains share `aws-waf-token` across subdomains (e.g. `.example.com`).
     */
    awsWaf: {
        challengeScriptUrl: process.env.NEXT_PUBLIC_AWS_WAF_CHALLENGE_SCRIPT_URL ?? '',
        cookieDomainList: parseHostnameList(process.env.NEXT_PUBLIC_AWS_WAF_COOKIE_DOMAIN_LIST),
    },
    cardinalcommerceUrl: process.env.NEXT_PUBLIC_CARDINALCOMMERCE_URL ?? '',
    joinB2CFormSessionDurationInSeconds: Number(
        process.env.NEXT_PUBLIC_JOIN_B2C_FORM_SESSION ?? 1200,
    ),
    revalidateAfterSeconds: Number(process.env.REVALIDATE_AFTER_SECONDS ?? 3600), // 1 hour
    loggedInUserSessionDuration: Number(
        process.env.NEXT_PUBLIC_LOGGED_IN_USER_SESSION_DURATION_IN_MINUTES ?? 20,
    ), // 20 minutes
    loggedInUserSessionExpiryPromptDuration: Number(
        process.env.NEXT_PUBLIC_LOGGED_IN_USER_SESSION_EXPIRY_PROMPT_DURATION_IN_MINUTES ?? 15,
    ), // 15 minutes
    cryptoPrivateKey: process.env.CRYPTO_PRIVATE_KEY ?? 'private-key-1',
    appDomain: process.env.APP_DOMAIN ?? '',
    searchSupertoken: {
        signInUrl: process.env.SEARCH_SUPERTOKENS_SIGNIN_URL ?? '',
        email: process.env.SEARCH_SUPERTOKENS_ADMIN_EMAIL ?? '',
        password: process.env.SEARCH_SUPERTOKENS_ADMIN_PASSWORD ?? '',
    },
    npdApiUrl: process.env.NEXT_PUBLIC_NPD_API_URL ?? '',
    newDashboardUsageHistoryTableRowCount: Number(
        process.env.NEXT_PUBLIC_NEW_DASHBOARD_USAGE_HISTORY_TABLE_ROW_COUNT ?? 5,
    ),
    outletPreBookUrl: process.env.NEXT_PUBLIC_OUTLET_PREBOOK_URL ?? '',
    baasJwtSecret: process.env.BAAS_JWT_SECRET ?? '',
    funnelSessionSecret: process.env.PP_FUNNEL_SESSION_SECRET ?? '',
    npdSourceCodesToFilterOutletCategoryEat:
        process.env.NPD_SOURCE_CODES_TO_FILTER_OUTLET_CATEGORY_EAT ?? '',
    sitemap: {
        sitemapLengthLimit: Number(process.env.SITEMAP_LENGTH_LIMIT ?? 12_000),
        region: process.env.SITEMAP_REGION ?? '',
        accessKeyId: process.env.SITEMAP_ACCESS_KEY_ID ?? '',
        secretAccessKey: process.env.SITEMAP_SECRET_ACCESS_KEY ?? '',
        bucket: process.env.SITEMAP_BUCKET ?? '',
        populateSecret: process.env.SITEMAP_POPULATE_SECRET ?? '',
    },
    isProductionEnvironment: process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod',
    isLocalhostAppDomain: process.env.APP_DOMAIN === 'localhost',
    serverCookieJwtSecret: process.env.SERVER_COOKIE_JWT_SECRET ?? '',
    /** HKDF salt for server cookie JWE key derivation. Keep stable across deploys. */
    serverCookieHkdfSalt: process.env.SERVER_COOKIE_HKDF_SALT ?? '',
    /** HKDF info/context for server cookie JWE key derivation. Keep stable across deploys. */
    serverCookieHkdfInfo: process.env.SERVER_COOKIE_HKDF_INFO ?? '',

    paymentWidgetIframeOrigin: process.env.NEXT_PUBLIC_PAYMENT_WIDGET_IFRAME_ORIGIN ?? '',
    linkedAccountGetDetailsApiUrl: `${process.env.NEW_LOGIN_API_URL}/profile/get-details`,
    linkedAccountCardsInformationApiUrl: `${process.env.NEW_LOGIN_API_URL}/payment/cards-information`,
    linkedAccountMembershipSourceCodesWithHiddenEntitlements: parseEnvStringToStringArray(
        process.env.LINKED_ACCOUNT_MEMBERSHIP_SOURCE_CODES_WITH_HIDDEN_ENTITLEMENTS,
    ),
    npdDealsThatCanViewActivationPopup: parseEnvStringToStringArray(
        process.env.NPD_DEALS_THAT_CAN_VIEW_ACTIVATION_POPUP,
    ),
    visaDiningOutletsToFilter: parseEnvStringToStringArray(
        process.env.VISA_DINING_OUTLETS_TO_FILTER,
    ),
    diApiProductCode: process.env.DI_API_PRODUCT_CODE ?? BrandProductCode.PP,
    isHttpsDomain: process.env.NEXT_PUBLIC_SITE_URL?.startsWith('https://') ?? false,
    validSourceCodesForAccorCampaign: parseEnvStringToStringArray(
        process.env.VALID_SOURCE_CODES_FOR_ACCOR_CAMPAIGN,
    ),
    mcaeOutletsToFilterLoggedOut: parseEnvStringToStringArray(
        process.env.MCAE_OUTLETS_TO_FILTER_LOGGED_OUT,
    ),
};
